import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  useDispatch,
} from 'react-redux';
import {
  toast,
} from 'react-toastify';

import {
  uiActions,
} from 'web-actions';

import {
  Icon,
  Popup,
  Button,
  Tooltip,
  Textarea,
} from 'web-components';

import {
  Flex,
  Text,
  SVG,
} from 'web-styled';

import * as S from './styled';

type Video = {
  type?: string,
  video_id?: string,
  video_url?: string,
  video_name?: string,
};

type Props = {
  showTooltip: boolean
  onSubmit: { (data: any, actions: any): void }
};

const Footer = ({ onSubmit, showTooltip }: Props) => {
  const [body, setBody] = useState('');
  const [video, setVideo] = useState<Video>({});
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [attachisOpen, setAttachisOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (showTooltip) {
      setTooltipIsOpen(true);
    }
  }, [showTooltip]);

  const handleAttachVideo = useCallback(() => {
    const onAssign = (v: any) => {
      setVideo({
        video_id: v.type === 'youtube' ? null : v.id,
        video_url: v.embed_url,
        video_name: v.name,
      });
      setBody(v.name);
    };
    const onUploaded = (videos: any[]) => {
      setVideo({
        video_id: videos[0].id,
        video_name: videos[0].name,
      });
      setBody(videos[0].name);
    };

    setAttachisOpen(false);

    const assignedId = (video.video_id || video.video_url || '').split('/').pop();
    dispatch(
      uiActions.searchVideoModal({
        query: '',
        modal: {
          title: 'Attach video',
          assignedId,
          assignButton: 'Send Video',
        },
        onAssign,
        onUploaded,
      }),
    );
  }, []);

  const handleSubmit = () => {
    if (!body) {
      toast.error("The body can't be blank");
      return;
    }

    setLoading(true);
    setTooltipIsOpen(false);

    const values: any = {
      body,
    };

    if (attachment) {
      values.attachment = attachment;
    }

    if (video?.video_id) {
      values.video_id = video.video_id;
    }

    if (video?.video_url) {
      values.video_url = video.video_url;
    }

    const cb = () => {
      setLoading(false);
      setBody('');
      setVideo({});
      setAttachment(null);
      setPhotoPreview(null);
    };

    onSubmit(
      values,
      {
        onSuccess: cb,
        onError: cb,
      },
    );
  };

  return (
    <S.Footer>
      <Flex
        width="100%"
        align="center"
        spacing={10}
        justify="space-between"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <Popup
          isOpen={attachisOpen}
          trigger={(
            <Flex
              width="44px"
              height="44px"
              align="center"
              justify="center"
              clickable
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width={18}
                height={18}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="primary-color"
              >
                <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
              </svg>
            </Flex>
          )}
          positions={['top']}
          setPopupVisible={() => setAttachisOpen(!attachisOpen)}
          onClickOutside={() => setAttachisOpen(false)}
        >
          <S.Actions>
            <S.Action
              padding="10px"

            >
              <SVG.Photo
                width="20"
                height="20"
              />
              <Text>Photo</Text>
              <input
                name="photo"
                type="file"
                accept="image/x-png,image/gif,image/jpeg"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files;
                  if (file && file[0]) {
                    setPhotoPreview(
                      URL.createObjectURL(
                        file[0],
                      ),
                    );
                    setAttachment(file[0]);
                  }
                  setAttachisOpen(false);
                }}
              />
            </S.Action>
            <S.Action onClick={handleAttachVideo}>
              <SVG.AddVideo
                width="19"
                height="19"
              />
              <Text>Video</Text>
            </S.Action>
          </S.Actions>
        </Popup>
        {video?.video_name && (
          <S.Video>
            <Text>
              {video.video_name}
            </Text>
            <Flex
              clickable
              onClick={() => {
                setVideo({});
              }}
            >
              <SVG.X size={13} />
            </Flex>
          </S.Video>
        )}

        <Tooltip
          body="Enter your first message and send."
          isOpen={tooltipIsOpen}
          positions={['top']}
        >
          <Flex width="calc(100% - 80px)">
            <Textarea
              value={body}
              minRows={1}
              autoFocus
              placeholder="Type your message here. Use Shift+Enter to add a new line."
              disabled={isLoading}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value || '';
                setBody(value);
                if (!value) {
                  setVideo({});
                }
              }}
            />
          </Flex>
        </Tooltip>
        <Button
          icon={<SVG.Send strokeWidth={2} size={13} />}
          appearance="primary"
          isLoading={isLoading}
          onClick={handleSubmit}
        />
      </Flex>
      <Flex align="center" spacing={15}>
        {photoPreview && (
          <Flex
            width="60px"
            height="60px"
            margin="15px 0 0 54px"
          >
            <S.Preview
              style={{
                backgroundImage: `url(${photoPreview})`,
              }}
            />
            <S.Remove
              onClick={() => {
                setAttachment(null);
                setPhotoPreview(null);
              }}
            >
              <SVG.X size={13} />
            </S.Remove>
          </Flex>
        )}
      </Flex>
    </S.Footer>
  );
};

export default Footer;
